import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { MultiSelect } from 'react-multi-select-component';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { FormGroup } from 'react-bootstrap';
import { customStyles, setImage, RightsAccess, createOptions } from '../../helper';
import noImage from '../../../assets/images/no-image.png';
import * as systemMasterAction from '../../../Pages/SystemMasters/Store/SystemMasters.action';
import AlignmentConfigComponent from '../../../Pages/Library/DPP/Library/ControlConfig/AlignmentConfig';
import { BoldConfig } from '../../../Pages/Library/DPP/Library/ControlConfig/BoldConfig';
import { ColorConfig } from '../../../Pages/Library/DPP/Library/ControlConfig/ColorConfig';

export const TestingControlSetting = forwardRef(({ obj, handleImageUpload }, ref) => {
  const { excludesCompanyRights } = RightsAccess();
  const [localState, setState] = useState(obj);
  const [error, setError] = useState({});

  const dispatch = useDispatch();
  const {
    SystemMastersDetails: { testTypeMaster },
  } = useSelector((redState) => redState);
  useEffect(() => {
    if (obj) {
      setState(obj);
    }
  }, [obj]);
  const { t } = useTranslation('common');
  const manualtestTypeOptions = [
    { label: 'Approved', value: 'approved' },
    { label: 'Pending', value: 'Pending' },
  ];

  const handleImageChange = (value, name, i) => {
    const cloneObjControls = cloneDeep(localState);
    cloneObjControls.testGroup[i][name] = value;
    setState((prev) => ({
      ...prev,
      ...cloneObjControls,
    }));
  };

  const validateTestingSetting = () => {
    const errMsg = 'This field is required';
    const cloneState = cloneDeep(localState);
    const errors = {};
    let hasError = false;
    cloneState?.testGroup?.forEach((err, i) => {
      if (!err?.testGroupTitle) {
        if (!errors[i]) {
          errors[i] = {};
        }
        hasError = true;
        errors[i].testGroupTitle = errMsg;
      }
      if (err?.testGroupType?.length) {
        err?.testGroupType?.forEach((vls, chi) => {
          if (vls?.testTypeAuto?.length === 0) {
            if (!errors[i]) {
              errors[i] = {};
            }
            if (!errors[i].testGroupType) {
              errors[i].testGroupType = [];
            }
            if (!errors[i].testGroupType[chi]) {
              errors[i].testGroupType[chi] = [];
            }
            hasError = true;
            errors[i].testGroupType[chi].testTypeAuto = errMsg;
          }
        });
      }
    });
    setError(errors);
    return hasError;
  };

  useImperativeHandle(ref, () => ({
    getLocalState: () => localState, // Expose a method to get local state
    updateTestingLocalState: (value, name, i) => handleImageChange(value, name, i), // Expose a method to update local state
    resetLocalState: () => setState({}), // Expose a method to reset local state
    validateTestingSetting: () => validateTestingSetting(), // Expose a method to reset local state
  }));

  useEffect(() => {
    if (!excludesCompanyRights) {
      dispatch(systemMasterAction.fetchTestTypeMaster());
    }
  }, [dispatch]);

  const testTypeOptions = useMemo(() => {
    if (testTypeMaster?.length) {
      return createOptions(testTypeMaster, 'name', '_id');
    }
    return [];
  }, [testTypeMaster?.length]);

  const handleChange = (value, name, chI, thrInd, frIn) => {
    const cloneObjControls = cloneDeep(localState);
    if (['alignment', 'colour', 'bold'].includes(name)) {
      cloneObjControls.testGroup[chI].testGroupType[thrInd].otherConfiguration[name] = value;
    }
    console.log('chIchIchI', chI, thrInd);
    console.log('namename', name);
    if (['descAlignment', 'descColour', 'descBold'].includes(name)) {
      console.log('Setting value for:', name);
      // Construct the key dynamically
      const dynamicKey = `${name}_${chI}_${thrInd}`;
      // Ensure the nested structure exists
      cloneObjControls.testGroup[chI].testGroupType[thrInd].descriptionConfiguration =
        cloneObjControls.testGroup[chI].testGroupType[thrInd].descriptionConfiguration || {};
      cloneObjControls.testGroup[chI].testGroupType[thrInd].descriptionConfiguration.otherConfiguration =
        cloneObjControls.testGroup[chI].testGroupType[thrInd].descriptionConfiguration.otherConfiguration || {};

      // Set the value
      cloneObjControls.testGroup[chI].testGroupType[thrInd].descriptionConfiguration.otherConfiguration[dynamicKey] = value;
    }
    console.log('cloneObjControlscloneObjControlscloneObjControls', cloneObjControls);
    if (thrInd >= 0 && chI >= 0 && frIn >= 0) {
      cloneObjControls.testGroup[chI].testGroupType[thrInd].testTypeAuto[frIn][name] = value;
    } else if (thrInd >= 0 && chI >= 0) {
      if (name === 'testTypeAuto') {
        cloneObjControls.testGroup[chI].testGroupType[thrInd].testTypeAuto = value;
      }
      // Access the correct index within the testGroupType array
      cloneObjControls.testGroup[chI].testGroupType[thrInd][name] = value;
    } else if (chI >= 0) {
      cloneObjControls.testGroup[chI][name] = value;
    } else {
      cloneObjControls[name] = value;
    }
    setState((prev) => ({
      ...prev,
      ...cloneObjControls,
    }));
  };

  const addNewTestGroup = (i, chI) => {
    const cloneObjControls = cloneDeep(localState);
    const alignmentConfig = cloneObjControls?.testGroup?.[i]?.testGroupType?.[chI]?.otherConfiguration;
    const newControlsObj = {
      testGroupTitle: '',
      testIconImage: '',
      testGroupType: [
        {
          otherConfiguration: {
            alignment: alignmentConfig?.alignment || 'center',
            colour: alignmentConfig?.colour || 'black',
            bold: alignmentConfig?.bold || false,
          },
          descriptionConfiguration: {
            otherConfiguration: {
              descAlignment_0_0: 'left',
              descColour_0_0: 'black',
              descBold_0_0: false,
            },
          },
          testTypeManual: '',
          testTypeAuto: [],
          testStatus: null,
          approvalDescription: '',
          approvalActive: false,
          approvalCopy: false,
          rejectedDescription: '',
          rejectedActive: false,
          rejectedCopy: false,
        },
      ],
    };
    cloneObjControls.testGroup.push(newControlsObj);
    setState((prev) => ({
      ...prev,
      ...cloneObjControls,
    }));
  };

  const addNewTestGroupType = (i, chI) => {
    const cloneObjControls = cloneDeep(localState);
    const alignmentConfig = cloneObjControls?.testGroup?.[i]?.testGroupType?.[chI]?.otherConfiguration;
    const newControlsObj = {
      otherConfiguration: {
        alignment: alignmentConfig?.alignment || 'center',
        colour: alignmentConfig?.colour || 'black',
        bold: alignmentConfig?.bold || false,
      },
      testTypeManual: '',
      testTypeAuto: [],
      testStatus: null,
      approvalDescription: '',
      approvalActive: false,
      approvalCopy: false,
      rejectedDescription: '',
      rejectedActive: false,
      rejectedCopy: false,
    };
    cloneObjControls.testGroup[i].testGroupType.push(newControlsObj);
    setState((prev) => ({
      ...prev,
      ...cloneObjControls,
    }));
  };

  function deleteTestGroupType(parentIndex, childIndex) {
    // Check if the parent exists
    const cloneObjControls = cloneDeep(localState);
    if (cloneObjControls?.testGroup?.[parentIndex]) {
      if (cloneObjControls?.testGroup?.[parentIndex]?.testGroupType && cloneObjControls?.testGroup?.[parentIndex]?.testGroupType[childIndex]) {
        cloneObjControls?.testGroup?.[parentIndex]?.testGroupType.splice(childIndex, 1);
      }
    }
    setState((prev) => ({
      ...prev,
      ...cloneObjControls,
    }));
  }

  function deleteTestGroup(parentIndex) {
    // Check if the parent exists
    const cloneObjControls = cloneDeep(localState);
    if (cloneObjControls?.testGroup?.[parentIndex]) {
      cloneObjControls?.testGroup?.splice(parentIndex, 1);
    }
    setState((prev) => ({
      ...prev,
      ...cloneObjControls,
    }));
  }

  const handleImageError = (event) => {
    event.target.src = noImage;
  };
  console.log('localState?.testGrouplocalState?.testGrouplocalState?.testGroup', localState?.testGroup);
  return localState?.testGroup?.map((testGro, chI) => {
    return (
      <>
        <div className="d-flex align-items-center">
          <div className="heading-icon-container mb-3">
            <figure>
              <img src={testGro?.testIconImage ? setImage(testGro?.testIconImage) : noImage} alt="" onError={handleImageError} />
            </figure>
            <button type="button" aria-label="Upload" onClick={() => handleImageUpload('testing_control', 'testIconImage', chI)} className="btn p-1">
              <i className="icon icon-upload f-14" />
            </button>
          </div>
        </div>
        <div className="position-relative">
          <div className="common-input-control flex-fill">
            <p className="f-16 text-medium field-name">
              {t('DPP.CONTROL.TESTING.GROUP_TITLE')}
              <span className="asterisk">*</span>
            </p>
            <input
              type="text"
              className="form-control"
              value={testGro?.testGroupTitle}
              placeholder={t('DPP.ENTER_TEXT')}
              onChange={(e) => handleChange(e.target.value, 'testGroupTitle', chI)}
              maxLength="40"
            />
            {error?.[chI]?.testGroupTitle && <span className="error-message">{error?.[chI]?.testGroupTitle}</span>}
          </div>
          {localState?.testGroup?.length - 1 === chI ? (
            <button type="button" aria-label="Upload" className="btn btn-outline btn-absolute" onClick={() => addNewTestGroup(chI, 0)}>
              <i className="icon-Add-bold" />
            </button>
          ) : (
            <button type="button" aria-label="Upload" className="btn btn-outline btn-absolute" onClick={() => deleteTestGroup(chI)}>
              <i className="icon-delete" />
            </button>
          )}
        </div>
        {testGro?.testGroupType?.map((testG, thrInd) => {
          return (
            <>
              <div className="d-flex">
                <div className={excludesCompanyRights ? 'flex-half pr-2' : 'flex-fill pr-2'}>
                  {excludesCompanyRights ? (
                    <div className="common-input-control mb-0">
                      <p className="field-name">
                        {t('DPP.CONTROL.TESTING.TEST_TYPE')}
                        <span className="asterisk">*</span>
                      </p>

                      <input
                        type="text"
                        className="form-control"
                        value={testG?.testTypeManual}
                        placeholder={t('DPP.ENTER_TEXT')}
                        onChange={(e) => handleChange(e.target.value, 'testTypeManual', chI, thrInd)}
                        maxLength="40"
                      />
                      <label htmlFor="configPlaceholder" className="asterisk-req">
                        {t('DPP.CONTROL.TESTING.TEST_TYPE')}
                        <span className="asterisk">*</span>
                      </label>
                    </div>
                  ) : (
                    <div className="common-react-select mb-0">
                      <p className="field-name">
                        {t('DPP.CONTROL.TESTING.TEST_TYPE')}
                        <span className="asterisk">*</span>
                      </p>

                      <MultiSelect
                        name="group"
                        options={testTypeOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        labelledBy="Select file type"
                        onChange={(selected) => {
                          handleChange(selected, 'testTypeAuto', chI, thrInd);
                        }}
                        value={testG?.testTypeAuto}
                      />
                      {error?.[chI]?.testGroupType?.[thrInd]?.testTypeAuto && (
                        <span className="error-message">{error?.[chI]?.testGroupType?.[thrInd]?.testTypeAuto}</span>
                      )}
                    </div>
                  )}
                </div>
                {excludesCompanyRights && (
                  <div className="flex-half pl-2 position-relative">
                    <p className="mb-1 f-16 text-medium">
                      {t('DPP.CONTROL.TESTING.TEST_STATUS')} <span className="asterisk">*</span>
                    </p>
                    <Select
                      placeholder={t('DPP.CONTROL.TESTING.TEST_STATUS_PLACEHOLDER')}
                      options={manualtestTypeOptions}
                      styles={customStyles}
                      getOptionLabel={({ label }) => `${label}`}
                      getOptionValue={({ value }) => `${value}`}
                      value={testG?.testStatus}
                      onChange={(selected) => handleChange(selected, 'testStatus', chI, thrInd)}
                    />
                    <span className="error-message">{error?.[chI]?.testGroupType?.[thrInd]?.testTypeAuto}</span>
                    {testGro?.testGroupType?.length - 1 === thrInd ? (
                      <button type="button" aria-label="Upload" className="btn btn-outline btn-absolute" onClick={() => addNewTestGroupType(chI, thrInd)}>
                        <i className="icon-add" />
                      </button>
                    ) : (
                      <button type="button" aria-label="Upload" className="btn btn-outline btn-absolute" onClick={() => deleteTestGroupType(chI, thrInd)}>
                        <i className="icon-delete" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {chI === 0 && (
                <div className="d-flex">
                  <AlignmentConfigComponent obj={testG} handleAlignment={(e) => handleChange(e.target.value, 'alignment', chI, thrInd)} dataName="alignment" />
                  <ColorConfig obj={testG} handleColor={(e) => handleChange(e.target.value, 'colour', chI, thrInd)} dataName="colour" />
                  <BoldConfig obj={testG} handleBold={(e) => handleChange(e.target.checked, 'bold', chI, thrInd)} dataName="bold" />
                </div>
              )}
              <FormGroup>
                <p className="mt-2 mb-1 f-16 text-bold">{t('DPP.CONTROL.TESTING.TESTING_DESCRIPTION')}</p>
                <div className="d-flex">
                  <AlignmentConfigComponent
                    obj={testG?.descriptionConfiguration}
                    handleAlignment={(e) => handleChange(e.target.value, 'descAlignment', chI, thrInd)}
                    dataName={`descAlignment_${chI}_${thrInd}`}
                  />
                  <ColorConfig
                    obj={testG?.descriptionConfiguration}
                    handleColor={(e) => handleChange(e.target.value, 'descColour', chI, thrInd)}
                    dataName={`descColour_${chI}_${thrInd}`}
                  />
                  <BoldConfig
                    obj={testG?.descriptionConfiguration}
                    handleBold={(e) => handleChange(e.target.checked, 'descBold', chI, thrInd)}
                    dataName={`descBold_${chI}_${thrInd}`}
                  />
                </div>
              </FormGroup>
              {!excludesCompanyRights &&
                testG?.testTypeAuto?.map((tes, frInd) => {
                  return (
                    <>
                      <p className="mt-2 mb-1 f-16 text-medium">{tes?.label}</p>
                      <div className="form-group mb-3 mt-2">
                        <div className="custom-control custom-switch">
                          <input
                            type="checkbox"
                            checked={tes?.approvalActive}
                            className="custom-control-input"
                            id={`approvalActive_${chI}_${thrInd}_${frInd}`}
                            onChange={(e) => {
                              handleChange(e.target.checked, 'approvalActive', chI, thrInd, frInd);
                            }}
                          />
                          <label className="custom-control-label mb-1" htmlFor={`approvalActive_${chI}_${thrInd}_${frInd}`}>
                            {tes?.label} {t('DPP.CONTROL.TESTING.APPROVAL_DESC')}
                          </label>
                        </div>
                        <textarea
                          name="comment"
                          className="form-control custom-textarea"
                          placeholder={t('DPP.ENTER_TEXT')}
                          value={tes?.approvalDescription}
                          onChange={(e) => {
                            handleChange(e.target.value, 'approvalDescription', chI, thrInd, frInd);
                          }}
                        />{' '}
                      </div>

                      <div className="form-group mt-1 mb-1">
                        <div className="custom-control custom-switch">
                          <input
                            type="checkbox"
                            checked={tes?.rejectedActive}
                            className="custom-control-input"
                            id={`rejectedActive_${chI}_${thrInd}_${frInd}`}
                            onChange={(e) => {
                              handleChange(e.target.checked, 'rejectedActive', chI, thrInd, frInd);
                            }}
                          />
                          <label className="custom-control-label" htmlFor={`rejectedActive_${chI}_${thrInd}_${frInd}`}>
                            {tes?.label} {t('DPP.CONTROL.TESTING.REJECTED_DESC')}
                          </label>
                        </div>
                        <textarea
                          name="comment"
                          className="form-control custom-textarea"
                          placeholder={t('DPP.ENTER_TEXT')}
                          value={tes?.rejectedDescription}
                          onChange={(e) => {
                            handleChange(e.target.value, 'rejectedDescription', chI, thrInd, frInd);
                          }}
                        />{' '}
                      </div>
                    </>
                  );
                })}
            </>
          );
        })}
      </>
    );
  });
});

export default TestingControlSetting;
